import React, { useEffect, useState } from 'react';
import API from '../utils/api';
import { toast } from 'react-toastify';
import ToggleSwitch from '../components/ToggleSwitch';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
const PAGE_SIZE = 12;
const EMPTY_FORM = { title: '', description: '', sourceType: 'youtube', url: '', order: 0, showInBanner: false };

export default function ManageVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewVideo, setViewVideo] = useState(null);

  const fetchVideos = (p = page) => {
    setLoading(true);
    API.get(`/videos/admin/list?page=${p}&limit=${PAGE_SIZE}`)
      .then(r => { setVideos(r.data.items); setTotalPages(r.data.totalPages); setPage(r.data.page); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { fetchVideos(1); }, []);

  const openAddForm = () => { setEditingId(null); setForm(EMPTY_FORM); setFile(null); setShowForm(true); };
  const openEditForm = (v) => {
    setEditingId(v._id);
    setForm({ title: v.title, description: v.description || '', sourceType: v.sourceType, url: v.sourceType === 'youtube' ? `https://www.youtube.com/watch?v=${v.videoId}` : '', order: v.order || 0, showInBanner: v.showInBanner });
    setFile(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title) return toast.error('Title is required');
    if (form.sourceType === 'youtube' && !form.url) return toast.error('YouTube URL is required');
    if (form.sourceType === 'upload' && !file && !editingId) return toast.error('Please choose a video file to upload');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append('file', file);
      if (editingId) {
        await API.put(`/videos/${editingId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Video updated');
      } else {
        await API.post('/videos', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Video added as draft — use the switch to publish it');
      }
      setShowForm(false); setFile(null); setEditingId(null); setForm(EMPTY_FORM);
      fetchVideos(editingId ? page : 1);
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this video?')) return;
    await API.delete(`/videos/${id}`);
    toast.success('Video removed');
    fetchVideos(videos.length === 1 && page > 1 ? page - 1 : page);
  };

  const togglePublish = async (v, nextValue) => {
    await API.put(`/videos/${v._id}`, { isPublished: nextValue });
    toast.success(nextValue ? 'Video published — now visible on the site' : 'Video unpublished');
    fetchVideos(page);
  };

  const toggleBanner = async (v, nextValue) => {
    await API.put(`/videos/${v._id}`, { showInBanner: nextValue });
    toast.success(nextValue ? 'Will now appear in the home banner slider' : 'Removed from the home banner slider');
    fetchVideos(page);
  };

  const thumbUrl = v => v.sourceType === 'youtube' ? `https://img.youtube.com/vi/${v.videoId}/hqdefault.jpg` : `${API_BASE}${v.url}`;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 className="admin-page-title" style={{ margin: 0 }}>🎥 Manage Videos</h2>
        <button className="btn btn-primary" onClick={() => (showForm ? setShowForm(false) : openAddForm())}>+ Add Video</button>
      </div>

      {showForm && (
        <div className="admin-form-card" style={{ marginBottom: 24 }}>
          <h3 style={{ fontWeight: 700, marginBottom: 16 }}>{editingId ? 'Edit Video' : 'Add Video'}</h3>
          <div className="form-group"><label className="form-label">Title *</label><input className="form-input" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} /></div>

          <div className="form-group">
            <label className="form-label">Source</label>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className={`btn btn-sm ${form.sourceType === 'youtube' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setForm(p => ({ ...p, sourceType: 'youtube' }))}>▶️ YouTube</button>
              <button type="button" className={`btn btn-sm ${form.sourceType === 'upload' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setForm(p => ({ ...p, sourceType: 'upload' }))}>⬆️ Upload File</button>
            </div>
          </div>

          {form.sourceType === 'youtube' ? (
            <div className="form-group"><label className="form-label">YouTube URL *</label><input className="form-input" value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} placeholder="https://www.youtube.com/watch?v=..." /></div>
          ) : (
            <div className="form-group"><label className="form-label">{editingId ? 'Replace Video File (optional)' : 'Video File *'}</label><input type="file" accept="video/*" onChange={e => setFile(e.target.files[0])} /></div>
          )}

          <div className="form-row">
            <div className="form-group"><label className="form-label">Description (Optional)</label><textarea className="form-input" rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Display Order</label><input className="form-input" type="number" value={form.order} onChange={e => setForm(p => ({ ...p, order: e.target.value }))} /></div>
          </div>

          <div className="form-group">
            <ToggleSwitch checked={form.showInBanner} onChange={v => setForm(p => ({ ...p, showInBanner: v }))} onLabel="Shown in Home Banner" offLabel="Not in Home Banner" />
          </div>

          {!editingId && <p style={{ fontSize: '0.82rem', color: '#888', marginBottom: 14 }}>New videos are saved as a draft. They won't appear on the site until you flip the switch to <strong>Published</strong>.</p>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={handleSave}>{editingId ? 'Save Changes' : 'Add Video'}</button>
            <button className="btn btn-outline" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="loader"><div className="spinner" /></div> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
            {videos.map(v => (
              <div key={v._id} style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', position: 'relative' }}>
                <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setViewVideo(v)}>
                  <img src={thumbUrl(v)} alt={v.title} style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block' }} />
                  <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.2rem', background: 'rgba(0,0,0,0.25)' }}>▶️</span>
                  <span style={{ position: 'absolute', top: 6, left: 6, background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: '0.7rem', padding: '2px 8px', borderRadius: 4 }}>{v.sourceType === 'youtube' ? 'YouTube' : 'Uploaded'}</span>
                </div>
                <div style={{ padding: '10px 12px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 8 }}>{v.title}</div>
                  <ToggleSwitch checked={v.isPublished} onChange={(val) => togglePublish(v, val)} />
                  <div style={{ marginTop: 8 }}>
                    <ToggleSwitch checked={v.showInBanner} onChange={(val) => toggleBanner(v, val)} onLabel="In Banner" offLabel="Not in Banner" />
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                    <button className="btn btn-sm btn-outline" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setViewVideo(v)}>👁️ View</button>
                    <button className="btn btn-sm btn-outline" style={{ flex: 1, justifyContent: 'center' }} onClick={() => openEditForm(v)}>✏️ Edit</button>
                  </div>
                </div>
                <button onClick={() => handleDelete(v._id)} style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(198,40,40,0.9)', color: '#fff', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', fontSize: '0.8rem' }}>✕</button>
              </div>
            ))}
            {videos.length === 0 && (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 40, color: '#888' }}>No videos added yet.</div>
            )}
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 28 }}>
              <button className="btn btn-sm btn-outline" disabled={page <= 1} onClick={() => fetchVideos(page - 1)}>← Prev</button>
              <span style={{ fontSize: '0.9rem', color: '#555' }}>Page {page} of {totalPages}</span>
              <button className="btn btn-sm btn-outline" disabled={page >= totalPages} onClick={() => fetchVideos(page + 1)}>Next →</button>
            </div>
          )}
        </>
      )}

      {viewVideo && (
        <div onClick={() => setViewVideo(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'pointer' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '90vw', maxWidth: 800 }}>
            {viewVideo.sourceType === 'youtube' ? (
              <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                <iframe src={`https://www.youtube.com/embed/${viewVideo.videoId}?autoplay=1`} title={viewVideo.title} frameBorder="0" allow="autoplay; encrypted-media" allowFullScreen style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', borderRadius: 10 }} />
              </div>
            ) : (
              <video src={`${API_BASE}${viewVideo.url}`} controls autoPlay style={{ width: '100%', maxHeight: '80vh', borderRadius: 10 }} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
